import { PillPropsType } from '@astro-ui/components/Pill'
import { ManualCurationUploadStatusType } from 'features/MultiCategory/@types/homeTab'
import { type ImageVariantIdType } from 'utils/apiList/images'
import { type FileVariantIdType } from 'utils/apiList/files'
import { type StatusType as BulkUploadStatusType } from 'utils/apiList/bulkUploadV2'
import { HomeTabCreateEditFormType } from 'features/MultiCategory/@types/homeTabCreateEdit'

export type HomeTabStepKeyType = 'informasi-tab' | 'kurasi-produk' | 'susunan-komponen'

export type HomeTabStepType = {
  key: HomeTabStepKeyType
  step: number
  label: string
}

/** A step as the wizard renders it — the tab strip also needs to know if it is reachable. */
export type HomeTabWizardStepType = HomeTabStepType & { isDisabled: boolean }

/**
 * Ordered steps for the create/edit Home Tab wizard.
 * Each step maps 1:1 to a URL segment (`:tab` param) so the active tab is
 * driven by the router. Content for each step is lazy-loaded (Suspense).
 */
export const HOME_TAB_STEPS: HomeTabStepType[] = [
  { key: 'informasi-tab', step: 1, label: 'Informasi Tab' },
  { key: 'kurasi-produk', step: 2, label: 'Kurasi Produk' },
  { key: 'susunan-komponen', step: 3, label: 'Susunan Komponen' },
]

export const DEFAULT_HOME_TAB_STEP: HomeTabStepKeyType = 'informasi-tab'

/**
 * Kurasi Produk and Susunan Komponen save through APIs that need the tab id, so
 * they stay locked until the tab exists — create only offers Informasi Tab.
 */
export const HOME_TAB_EDIT_ONLY_STEPS: HomeTabStepKeyType[] = ['kurasi-produk', 'susunan-komponen']

/**
 * Automated curation (criteria builder + its dialog) ships in a later phase.
 * The section and its wiring stay in place — flip this to `true` to bring it back.
 */
export const IS_AUTOMATED_CURATION_ENABLED = false

export const HOME_TAB_CREATE_BASE_PATH = '/dashboard/multicategory/create'
export const HOME_TAB_EDIT_BASE_PATH = '/dashboard/multicategory/edit'

export const SCHEDULE_TYPE_OPTIONS = [
  { name: 'During (periode tertentu)', value: 'during' },
  { name: 'Recurring (berulang)', value: 'recurring' },
]

/** `targeting_type_id` — create/update accept 1 (Normal), 3 (Targeted), 4 (Segmented). */
export const HOME_TAB_TARGETING_TYPE = {
  NORMAL_USER: 1,
  TARGETED_USER: 3,
  SEGMENTED_USER: 4,
} as const

/** `homepage_tabbing_component_type_id` — master list from `/homepage-tabbings/component-types`. */
export const HOME_TAB_COMPONENT_TYPE = {
  BANNER_REGULAR: 1,
} as const

/** `type_id` on `targeted_segmentations` — 13 identifies Homepage Tabbing. */
export const HOME_TAB_SEGMENTATION_TYPE_ID = 13

/** `type_id` on a component's `targeted_segmentations` — 14 identifies its components. */
export const HOME_TAB_COMPONENT_SEGMENTATION_TYPE_ID = 14

/** The tab icon must be exactly this many pixels square — the file service rules carry no dimensions. */
export const HOME_TAB_ICON_DIMENSION = 96

/** File-service variant used to upload the tab icons. */
export const HOME_TAB_ICON_FILE_VARIANT_ID: FileVariantIdType = 'multiple-homepage-tabbing-icon'

/** Image-service variant used for a Category Specific tile's artwork. */
export const CATEGORY_SPECIFIC_TILE_IMAGE_VARIANT_ID: ImageVariantIdType =
  'category-specific-tile-image'

/** `image_type` on the detail API's `images[]`. */
export const HOME_TAB_IMAGE_TYPE = {
  SELECTED: 1,
  UNSELECTED: 2,
} as const

export const TABBING_DISPLAY_NAME_MAX_LENGTH = 10

/** Highest slot a tab can take on the homepage. */
export const TABBING_SLOT_MAX = 5

/** Tabbing Type a Default tab is pinned to. */
export const DEFAULT_VARIANT_TABBING_TYPE_ID = '5'

export const HOME_TAB_FORM_DEFAULT_VALUES: HomeTabCreateEditFormType = {
  variant: 'Default',
  tabbingName: '',
  tabbingDisplayName: '',
  tabbingType: DEFAULT_VARIANT_TABBING_TYPE_ID,
  tabbingSlot: '',
  iconUnselected: null,
  iconSelected: null,
  iconUnselectedUrl: '',
  iconSelectedUrl: '',
  scheduleType: 'during',
  startSchedule: '',
  endSchedule: '',
  targetedUser: String(HOME_TAB_TARGETING_TYPE.NORMAL_USER),
  targetedCustomers: [],
  targetedSegments: [],
  hubs: [],
}

/** `location_ids` carries this single id to mean "Semua Hub" (every hub). */
export const ALL_HUB_ID = 0

/** `tabbing_category` — drives the Informasi Umum variant radio. */
export const HOME_TAB_CATEGORY = {
  DEFAULT: 1,
  STANDARD: 2,
} as const

export const HOME_TAB_VARIANT_BY_CATEGORY: Record<number, HomeTabCreateEditFormType['variant']> = {
  [HOME_TAB_CATEGORY.DEFAULT]: 'Default',
  [HOME_TAB_CATEGORY.STANDARD]: 'Standard',
}

/** Bulk-upload `status` → the label + pill theme the Riwayat table shows. */
export const MANUAL_CURATION_STATUS_MAP: Record<
  BulkUploadStatusType,
  { label: ManualCurationUploadStatusType; theme: PillPropsType['theme'] }
> = {
  SUCCESS: { label: 'Sukses', theme: 'success' },
  FAILED: { label: 'Gagal', theme: 'danger' },
  ON_PROGRESS: { label: 'Diproses', theme: 'warning' },
  PARTIAL_SUCCESS: { label: 'Sukses Sebagian', theme: 'info' },
}

export const getManualCurationStatusPillColor = (
  status: ManualCurationUploadStatusType,
): PillPropsType['theme'] =>
  Object.values(MANUAL_CURATION_STATUS_MAP).find(({ label }) => label === status)?.theme ||
  'secondary'
